<template>
    <template v-if="isLoading">
        <AppLoading :loading="isLoading" :text="$t('Loading') + '...'" />
    </template>
    <div
        v-if="!isLoading && auth_user && hasPermission(auth_user.can, 'create.device')"
        class="row align-items-end mb-5"
    >
        <div class="col-sm-auto">
            <AppButton
                @click="toggleAdd"
                :isLoading="isCheckingActiveDevice"
                :disabled="isLoading || isCheckingActiveDevice"
            >
                <AppIcon :icon="faPlusCircle" />
                {{ $t('Add Device') }}
            </AppButton>
        </div>
    </div>
    <template v-if="machines && auth_user && selected">
        <DeviceSelector
            class="pb-3"
            :histories="machines"
            :context="context"
            :auth_user="auth_user"
            :patient="patient"
            v-model="selected"
            @update:modelValue="reset"
            @machine:delete="onDeleteMachine"
            @machine:update="onUpdateMachine"
            @machine:add-pin="onAddPin"
            @machine:icode="openIcodeReportsModal"
        />
        <div v-show="showDeviceInfo" class="d-flex flex-column">
            <template v-if="selected">
                <DeviceSettingsAndStatus
                    :machine="selected" class="pb-3"
                    :user_org="user_org"
                    :patient_org="patient?.organization"
                    @done="() => {
                        showDeviceInfo = true;
                        deviceDatesChanged = false;
                    }"
                    :auth_user="auth_user"
                />
            </template>
            <DeviceModule v-show="showDeviceInfo" :machine="selected" class="pb-3" />
        </div>

        <ICodeReportsModal
            v-if="patient"
            v-model="showIcodeReports"
            :patient="patient"
            :device="selected"
            size="xl"
        />
    </template>
    <template v-if="failure">
        <span class="text-secondary"><i>{{ $t('No devices found for this patient.') }}</i></span>
    </template>
    <AppModal v-model="isAddDeviceVisible"  @update:modelValue="onCloseModal" size="sm">
        <template #header>
            <h3 class="modal-title">
                <template v-if="isUpdateForm">
                    {{ $t('Update equipment') }}
                </template>
                <template v-if="isPinForm">
                    {{ $t('Add Pin') }}
                </template>
                <template v-if="!isPinForm && !isUpdateForm">
                    {{ $t('Add equipment') }}
                </template>
            </h3>
        </template>
        <template #default>
            <form @submit.prevent="submit">
                <fieldset>
                    <div class="row">
                        <div class="mb-3 col-md-12">
                            <AppDatepicker
                                v-model="computedForm.start_date"
                                :placeholder="$t('Start Date')"
                                class="col-12"
                                :emitOnChange="true"
                                :readonly="isPinForm"
                                @update:modelValue="() => {
                                    computedForm.validate('start_date');
                                    deviceDatesChanged = true;
                                }"
                                format-model="YYYY-MM-DD"
                            />
                            <div class="mt-2 text-danger" v-if="computedForm.invalid('start_date')">
                                {{ computedForm.errors.start_date }}
                            </div>
                        </div>
                    </div>
                </fieldset>

                <template v-if="isUpdateForm" >
                    <fieldset>
                        <div class="row">
                            <div class="mb-3 col-md-12">
                                <AppDatepicker
                                    v-model="computedForm.end_date"
                                    :placeholder="$t('End Date')"
                                    class="col-12"
                                    :emitOnChange="true"
                                    :readonly="isPinForm"
                                    @update:modelValue="() => {
                                        computedForm.validate('end_date');
                                        deviceDatesChanged = true;
                                    }"
                                    format-model="YYYY-MM-DD"
                                />
                                <div class="mt-2 text-danger" v-if="computedForm.invalid('end_date')">
                                    {{ computedForm.errors.end_date }}
                                </div>
                            </div>
                        </div>
                    </fieldset>
                </template>

                <fieldset>
                    <div class="row">
                        <div v-if="!isUpdateForm && !isPinForm" class="mb-3 col-md-12">
                            <label class="fs-6 mb-0 --form-label --mb-1" for="serial_number">
                                {{ $t('Serial Number') }}
                            </label>
                            <input
                                id="serial_number"
                                type="text"
                                class="form-control form-control-sm"
                                v-model="computedForm.serial_number"
                                :readonly="isUpdateForm || isPinForm"
                                @input="computedForm.serial_number = $event.target.value.toUpperCase()"
                                @change="computedForm.validate('serial_number');"
                            />
                            <div class="mt-2 text-danger" v-if="computedForm.invalid('serial_number')">
                                {{ computedForm.errors.serial_number }}
                            </div>
                        </div>
                        <div v-if="isUpdateForm || isPinForm" class="mb-3 col-md-12">
                            <label class="fs-6 mb-0 --form-label --mb-1" for="serial_number">
                                <strong>{{ $t('Serial Number') }}</strong> {{ computedForm.serial_number }}
                            </label>
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div class="row">
                        <div v-if="!isUpdateForm" class="mb-3 col-md-12">
                            <label class="fs-6 mb-0 --form-label --mb-1" for="pin_number">{{ $t('Pin Number') }}</label>
                            <input
                                id="pin_number"
                                type="pin_number"
                                class="form-control form-control-sm"
                                v-model="computedForm.pin_number"
                                @change="computedForm.validate('pin_number')"
                            />
                            <div class="mt-2 text-danger" v-if="computedForm.invalid('pin_number')">
                                {{ computedForm.errors.pin_number }}
                            </div>
                        </div>
                        <div v-if="isUpdateForm" class="mb-3 col-md-12">
                            <label class="fs-6 mb-0 --form-label --mb-1" for="pin_number">
                                <strong>{{ $t('Pin Number') }}</strong>
                                {{ computedForm.pin_number }}
                            </label>
                        </div>
                    </div>
                </fieldset>
            </form>
        </template>
        <template #footer>
            <AppButton btnClass="btn-white"  @click="() => isAddDeviceVisible = false">
                <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
            </AppButton>
            <AppButton btnClass="btn-primary" @click="validationStep1()" :is-loading="isSubmiting">
                <i class="bi bi-save me-1"></i>
                <template v-if="isUpdateForm">
                    {{ $t('Update') }}
                </template>
                <template v-else>
                    {{ $t('Save') }}
                </template>
            </AppButton>
        </template>
    </AppModal>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useForm } from 'laravel-precognition-vue';
import { useStore } from "@/vue/provideGlobalStoreState";
const { setPatientDeviceSerials } = useStore();
import DeviceSelector from "../devices/DeviceHistorySelector.vue";
import DeviceSettingsAndStatus from "../devices/DeviceSettingsAndStatus.vue";
import DeviceModule from "@/vue/connect/devices/DeviceModule.vue";
import AppButton from "@/vue/shared/AppButton.vue";
import AppModal from "@/vue/shared/AppModal.vue";
import AppDatepicker from "../../shared/AppDatepicker.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import AppAlert from "@/vue/shared/dialog/AppAlert.vue";
import AppConfirm from "@/vue/shared/dialog/AppConfirm.vue";
import usePermissions from "@/vue/composables/usePermissions";
import moment from "moment";
import {trans as $t} from "@/vue/composables/missingTranslations";
import AppLoading from "@/vue/shared/AppLoading.vue";
import { deviceDatesOverlapErrorMessage } from "@/vue/connect/patients/helpers";
import ICodeReportsModal from "@/vue/connect/icode/ICodeReportsModal.vue";
import AppIcon from "@/vue/shared/AppIcon.vue";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { usePatientDevicesStore } from "@/vue/store/patientDevicesStore";
import { usePatientStore } from "@/vue/store/patientStore";
import { storeToRefs } from "pinia";
const {reveal: alert} = createConfirmDialog(AppAlert);
const {reveal: confirm} = createConfirmDialog(AppConfirm);
const { hasPermission } = usePermissions();
const patientStore = usePatientStore();

const props = defineProps({
    context: {
        type: String,
        default: 'patient',
    },
    auth_user: {
        type: [Object, null],
        required: true,
    },
    patient: {
        type: Object,
        required: true,
    }
});

// Therapy has started and Equipment Start Date is no more than 90 days from Therapy Start Date
const isWithinInitialPeriod = computed(() => {
    if (!props.patient.therapy_start || !computedForm.value.start_date || !props.patient.compliance_met_result) {
        return false;
    }

    const startDate = moment(computedForm.value.start_date);
    const therapyStart = moment(props.patient.therapy_start);
    const daysDiff = startDate.diff(therapyStart, 'days');

    return daysDiff <= props.patient.compliance_met_result.period && daysDiff >= -Infinity;
});

const inOngoingPeriod = computed(() => {
    if( props.patient.compliance_ongoing_result ) return true;                      // Already have ongoing

    if( !props.patient.compliance_met_result ) return false;                        // Need initial

    if( props.patient.compliance_met_result.compliance_met_date ) return true;      // Already met initial

    const therapyStart = moment(props.patient.therapy_start);
    const daysDiff = moment().diff(therapyStart, "days");
    return daysDiff > props.patient.compliance_met_result.period;                   // In ongoing if therapy is past period
});

const showIcodeReports = ref(false);
const openIcodeReportsModal = () => {
    showIcodeReports.value = true;
};

const computedForm = computed(() => {
    if (isUpdateForm.value) {
        return useForm("patch", `/api/patients/${props.patient.id}/check-update-device/${selected.value.machine_id}/`, {
            serial_number: selected.value.serial,
            pin_number: selected.value.pin,
            start_date: selected.value.start_date,
            end_date: selected.value.end_date,
        });
    } else if (isPinForm.value) {
        return useForm("post", `/api/patients/${props.patient.id}/devices/${selected.value.machine_id}/check-add-pin`, {
            serial_number: selected.value.serial,
            pin_number: selected.value.pin,
            start_date: selected.value.start_date
        });
    }
    return useForm("post", `/api/patients/${props.patient.id}/check-add-device`, {
        serial_number: "",
        pin_number: "",
        start_date: ""
    });
});


const savePin = async () => {
    try {
        await axios.post(`/api/patients/${props.patient.id}/devices/${selected.value.id}/add-pin`, {
            start_date: computedForm.value.start_date,
            pin_number: computedForm.value.pin_number,
            serial_number: computedForm.value.serial_number,
        });
        isPinForm.value = false;
        isAddDeviceVisible.value = false;
        selected.value = null;
        await FetchDevices(props.patient.id);
    } catch (e) {
        if (e.response?.data?.errors?.pin_number) {
            computedForm.value.setErrors(e.response.data.errors);
        }
    }
}

const validationStep1 = async () => {
    isSubmiting.value = true;
    try {
        await computedForm.value.submit();
    } catch (error) {
        isSubmiting.value = false;
        return;
    }
    isSubmiting.value = false;
    if (isPinForm.value) {
        savePin();
        return;
    }
    if (isUpdateForm.value && selected.value.pin !== computedForm.value.pin_number && computedForm.value.pin_number.trim() === '') {
        const confirm = await confirmWindow($t("Warning !"), $t('messages.pin_not_removed'));
        if (confirm.isCanceled) {
            return;
        }
    }
    if (isWithinInitialPeriod.value) {
        const confirm = await confirmWindow($t('Are you sure ?'), $t('messages.confirm_assigned_date_change'));
        if (confirm.isCanceled) {
            isSubmiting.value = false;
            return;
        }
    }
    isSubmiting.value = true;
    try {
        await axios.post(`/api/patients/${props.patient.id}/distance-from-today`, {
            start_date: computedForm.value.start_date,
            serial_number: computedForm.value.serial_number,
            pin_number: computedForm.value.pin_number,
        });

        isSubmiting.value = false;
        validationStep2();
    } catch (error) {
        isSubmiting.value = false;
        if (error?.response?.data?.errors) {
            let message = '';
            Object.keys(error.response.data.errors).forEach(key => {
                if (Array.isArray(error.response.data.errors[key])) {
                    message = error.response.data.errors[key].shift();
                }
            });
            const confirm = await confirmWindow("Are you sure ?", message);
            if (!confirm.isCanceled) {
                validationStep2();
            }
        }
    }
}

const validationStep2 = async () => {
    isSubmiting.value = true;
    try {
        let url = `/api/patients/${props.patient.id}/check-date-overlaps`;
        let form = {
            start_date: computedForm.value.start_date,
            serial_number: computedForm.value.serial_number,
            pin_number: computedForm.value.pin_number,
        };
        if (isUpdateForm.value) {
            url = `/api/patients/${props.patient.id}/devices/${selected.value.id}/check-date-overlaps`;
            form = {
                start_date: computedForm.value.start_date,
                end_date: computedForm.value.end_date,
                serial_number: computedForm.value.serial_number,
                pin_number: computedForm.value.pin_number,
            };
        }
        await axios.post(url, form);
        isSubmiting.value = false;
        submit();
    } catch (error) {
        isSubmiting.value = false;
        if (error?.response?.status === 422) {
            if (error?.response?.data?.error === 'patient-already-connected') {
                alertWindow($t("Device Start Date Overlaps"), $t('messages.single_active_device_warning'));
            } else if (error?.response?.data?.error === 'patient-previous-device-daterange-overlaps') {
                alertWindow($t("Device Start Date Overlaps"), $t('messages.date_range_overlap_warning'));
            } else if (error?.response?.data?.error === 'device-already-connected') {
                alertWindow($t("Device Start Date Overlaps"), deviceDatesOverlapErrorMessage(error?.response?.data?.data?.machineHistory));
            } else if (error?.response?.data?.error === 'device-start-date-overlaps') {
                alertWindow($t("Device Start Date Overlaps"), deviceDatesOverlapErrorMessage(error?.response?.data?.data?.machineHistory));
            }
        }
    }
};

const submit = async () => {
    isSubmiting.value = true;
    try {
        let url = `/api/patients/${props.patient.id}/devices`;
        let form = {
            start_date: computedForm.value.start_date,
            serial_number: computedForm.value.serial_number,
            pin_number: computedForm.value.pin_number,
        };
        if (isUpdateForm.value) {
            url = `/api/patients/${props.patient.id}/devices/${selected.value.id}`;
            form = {
                ...form,
                end_date: computedForm.value.end_date,
            };
            await axios.patch(url, form);
        } else {
            await axios.post(url, form);
        }
        if( deviceDatesChanged.value ) {
            if( isWithinInitialPeriod.value || !props.patient.compliance_met_result ) {
                await axios.post(`/api/dashboard/initial-compliance/patients/${props.patient.id}/compliance`);
                await patientStore.getPatient(props.patient.id);
            }
            if( inOngoingPeriod.value ) {
                await axios.post(`/api/dashboard/ongoing-compliance/patients/${props.patient.id}`);
                await patientStore.getPatient(props.patient.id);
            }
        }
        if (!isUpdateForm.value) {
            computedForm.value.reset();
        }
        isSubmiting.value = false;
        isAddDeviceVisible.value = false;
        await FetchDevices(props.patient.id);
        selected.value = machines.value[0];
    } catch (error) {
        isSubmiting.value = false;
        if (error.response?.data?.errors?.max_failed_attempts_reached) {
            computedForm.value.setErrors(error.response.data.errors);
            await alertWindow($t("Invalid Device Pin"), error.response.data.errors?.max_failed_attempts_reached);
        } else if (error.response?.data?.errors?.max_failed_attempts_overpassed) {
            window.location.reload();
        } else if (error.response?.data?.errors?.pin_number) {
            computedForm.value.setErrors(error.response.data.errors);
        }
    }
};

const user_org = ref(null);
const selected = ref(null);
const failure = ref(false);
const isCheckingActiveDevice = ref(false);
const isSubmiting = ref(false);
const isUpdateForm = ref(false);
const isPinForm = ref(false);
const isAddDeviceVisible = ref(false);
const deviceDatesChanged = ref(false);

const patientDevicesStore = usePatientDevicesStore();
const { FetchDevices } = patientDevicesStore;
const { devices: machines, isLoading } = storeToRefs(patientDevicesStore);

watch(
    () => machines.value,
    () => {
        if (machines.value.length === 0) {
            showDeviceInfo.value = false;
            selected.value = null;
            failure.value = true;
        } else if (!selected.value) {
            selected.value = machines.value[0];
        }

        // TODO could be moved to pinia store
        const activeSerials = machines.value.map(machine => {
            if (!machine.end_date && machine.serial) {
                return machine.serial;
            }
        });
        setPatientDeviceSerials(activeSerials);
    }
);

const showDeviceInfo = ref(false);
onMounted(async () => {
    await FetchDevices(props.patient.id);

    axios.get("/api/users/my-org").then(response => {
        user_org.value = response.data;
    });
});

function reset() {
    showDeviceInfo.value = false;
}

const onAddPin = async ($history) => {
    isPinForm.value = true;
    isAddDeviceVisible.value = true;
}

const hideName = (name, length) => {
    const charatersLength = name.length - length;
    let hiddenChars = '';
    for (let i = 0; i < charatersLength; i++) {
        hiddenChars += '*';
    }
    return name.substring(0, length) + hiddenChars;
}

const onDeleteMachine = async ($history) => {
    const confirm1 = await confirmWindow($t("Delete Device ?"), $t('messages.confirm_device_deletion'));
    if (confirm1.isCanceled) {
        return;
    }
    const confirm2 = await confirmWindow($t("Are you sure ?"), $t('messages.final_confirm_record_deletion'));
    if (confirm2.isCanceled) {
        return;
    }
    isSubmiting.value = true;
    try {
        await axios.delete(`/api/patients/${props.patient.id}/devices/${$history.id}`);
        isSubmiting.value = false;
        await FetchDevices(props.patient.id);
    } catch (e) {
        isSubmiting.value = false;
    }
}

const onUpdateMachine = ($event) => {
    isUpdateForm.value = true;
    isAddDeviceVisible.value = true;
}

const onCloseModal = (state) => {
    if (!state) {
        isUpdateForm.value = false;
        isPinForm.value = false;
        if (isSubmiting.value) {
            isSubmiting.value = false;
        }
    }
}

const alertWindow = async (title, message) => {
    return await alert({
            title: title,
            message: message,
            confirmButtonText: $t("Close")
        }
    );
}

const confirmWindow = async (title, message) => {
    return await confirm({
            title: title,
            message: message,
            confirmButtonText: $t("Ok"),
            cancelButtonText: $t("Close")
        }
    );
}

async function toggleAdd() {
    isCheckingActiveDevice.value = true;

    try {
        await axios.get(`/api/patients/${props.patient.id}/check-if-active-devices`);

        isUpdateForm.value = false;
        isPinForm.value = false;
        isAddDeviceVisible.value = true;
    } catch (e) {
        await FetchDevices(props.patient.id);
        await alertWindow($t("Add Device"), $t('messages.single_active_device_warning'));
    } finally {
        isCheckingActiveDevice.value = false;
    }
}
</script>
